// @ts-nocheck
/**
 * useSafetyMetrics Hook - FERP Repository Pattern Implementation
 *
 * React hook for executive safety metrics with:
 * - Real-time subscription to safety score updates
 * - Tenant isolation using getCurrentTenantId
 * - Life-critical safety scores from backend calculation
 * - ZERO MOCK DATA: All metrics from Redis audit trails
 */

import { useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { type ApolloClient } from '@apollo/client';
import {
  SafetyMetricsRepository,
  ExecutiveSafetyMetrics,
} from '../data/repositories/SafetyMetricsRepository';
import { useAuth } from './useAuth';

/**
 * Configuration options for useSafetyMetrics hook
 */
export interface UseSafetyMetricsOptions {
  /** Time window for metric calculation in seconds (default: 86400 = 24 hours) */
  timeWindowSecs?: number;

  /** Enable real-time subscription updates (default: true) */
  enableSubscription?: boolean;

  /** Tenant ID override (default: from useAuth) */
  tenantId?: string;
}

/**
 * Return type for useSafetyMetrics hook
 */
export interface UseSafetyMetricsResult {
  /** Executive safety metrics calculated from audit trails */
  metrics: ExecutiveSafetyMetrics | null;

  /** Loading state for initial query */
  loading: boolean;

  /** Error state if query/subscription fails */
  error: Error | null;

  /** Manually refetch safety metrics */
  refetch: () => Promise<void>;

  /** Unix timestamp of last update */
  lastUpdated: number | null;
}

/**
 * Hook for fetching and subscribing to executive safety metrics
 *
 * FERP Pattern: Repository-based data access with real-time updates
 * LIFE-CRITICAL: Safety scores from audit trail analysis
 *
 * @param options - Configuration options
 * @returns Safety metrics state and controls
 *
 * @example
 * ```tsx
 * const { metrics, loading, error, refetch } = useSafetyMetrics({
 *   timeWindowSecs: 86400, // 24 hours
 *   enableSubscription: true
 * });
 *
 * if (loading) return <Loader />;
 * if (error) return <Alert>{error.message}</Alert>;
 * if (!metrics) return <Alert>No metrics available</Alert>;
 *
 * return (
 *   <SafetyDashboard
 *     overallScore={metrics.overallSafetyScore}
 *     riskLevel={metrics.riskLevel}
 *     trend={metrics.trend}
 *   />
 * );
 * ```
 */
export const useSafetyMetrics = (
  options: UseSafetyMetricsOptions = {}
): UseSafetyMetricsResult => {
  const {
    timeWindowSecs = 86400, // Default 24 hours
    enableSubscription = true,
    tenantId: tenantIdOverride,
  } = options;

  const { getCurrentTenantId } = useAuth();
  const tenantId = tenantIdOverride || getCurrentTenantId();
  const client = useApolloClient() as ApolloClient;

  const [metrics, setMetrics] = useState<ExecutiveSafetyMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  /**
   * Fetch safety metrics from backend
   */
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await SafetyMetricsRepository.findByTenant(
        client,
        tenantId,
        timeWindowSecs
      );

      setMetrics(result);
      setLastUpdated(result.lastUpdated);
      setError(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch safety metrics');
      setError(error);
      console.error('Safety metrics fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [client, tenantId, timeWindowSecs]);

  /**
   * Initial fetch on mount or when dependencies change
   */
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Subscribe to real-time safety metrics updates
   */
  useEffect(() => {
    if (!enableSubscription) {
      return undefined;
    }

    const unsubscribe = SafetyMetricsRepository.subscribeToUpdates(
      client,
      tenantId,
      (updatedMetrics) => {
        setMetrics(updatedMetrics);
        setLastUpdated(updatedMetrics.lastUpdated);
        setError(null);
      },
      timeWindowSecs
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [client, tenantId, timeWindowSecs, enableSubscription]);

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics,
    lastUpdated,
  };
};
